import React, { useEffect, useMemo, useState, createElement } from 'react';
import { PercentIcon, SaveIcon, FilterIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Tabs } from '../ui/Tabs';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Pagination } from '../ui/Pagination';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { usePagination } from '../../hooks/usePagination';
import { supabase } from '../../utils/supabase';
import { formatCurrency } from '../../utils/helpers';
// ── EXPORT PDF ────────────────────────────────────────────────────────────────
function exportCommissionsToPdf(aptRows: any[], productRows: any[], dateFrom: string, dateTo: string, totalServices: number, totalProducts: number, totalGeneral: number) {
  const fmt = (n: number) => n.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP'
  });
  const today = new Date().toLocaleDateString('es-DO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const aptTableRows = aptRows.map((r, i) => `
      <tr class="${i % 2 === 0 ? 'even' : ''}">
        <td>${r.date}</td>
        <td>${r.patient}</td>
        <td>${r.doctor}</td>
        <td>${r.service}</td>
        <td class="right">${fmt(r.total)}</td>
        <td class="right amount">${fmt(r.commission)}</td>
        <td class="center"><span class="badge ${r.type === 'Doctor' ? 'badge-green' : 'badge-blue'}">${r.type}</span></td>
        <td>${r.userName}</td>
      </tr>`).join('');
  const prodTableRows = productRows.map((r, i) => `
      <tr class="${i % 2 === 0 ? 'even' : ''}">
        <td>${r.date}</td>
        <td>${r.product}</td>
        <td class="right">${fmt(r.total)}</td>
        <td class="right amount">${fmt(r.commission)}</td>
        <td>${r.userName}</td>
      </tr>`).join('');
  const cssText = ['* { margin: 0; padding: 0; box-sizing: border-box; }', 'body { font-family: Arial, sans-serif; font-size: 10px; color: #1f2937; background: #fff; }', '.header { background: linear-gradient(135deg, #0d9488, #0f766e); color: white; padding: 28px 36px 22px; }', '.header h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }', '.header p { font-size: 10px; opacity: 0.85; }', '.header-meta { display: flex; gap: 24px; margin-top: 12px; }', '.header-meta span { background: rgba(255,255,255,0.15); border-radius: 20px; padding: 3px 12px; font-size: 9px; font-weight: 600; }', '.summary { display: flex; gap: 12px; padding: 20px 36px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }', '.summary-card { flex: 1; background: white; border-radius: 10px; padding: 14px 18px; border-left: 4px solid #0d9488; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }', '.summary-card.purple { border-left-color: #7c3aed; }', '.summary-card.pink { border-left-color: #db2777; }', '.summary-card label { display: block; font-size: 8.5px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }', '.summary-card strong { font-size: 15px; font-weight: 700; color: #111827; }', '.section { padding: 18px 36px 0; }', '.section-title { font-size: 11px; font-weight: 700; color: #0f766e; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #0d9488; display: flex; align-items: center; gap: 6px; }', '.section-title::before { content: ""; display: inline-block; width: 8px; height: 8px; background: #0d9488; border-radius: 50%; }', 'table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }', 'thead tr { background: #134e4a; color: white; }', 'thead th { padding: 8px 10px; text-align: left; font-size: 8.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }', 'thead th.right { text-align: right; }', 'thead th.center { text-align: center; }', 'tbody td { padding: 7px 10px; font-size: 9.5px; color: #374151; border-bottom: 1px solid #f1f5f9; }', 'tbody tr.even td { background: #f8fafc; }', 'td.right { text-align: right; }', 'td.center { text-align: center; }', 'td.amount { color: #059669; font-weight: 700; }', '.badge { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 8px; font-weight: 600; }', '.badge-green { background: #d1fae5; color: #065f46; }', '.badge-blue { background: #dbeafe; color: #1e40af; }', '.empty-row td { text-align: center; color: #9ca3af; font-style: italic; padding: 16px; }', '.footer { margin-top: 24px; background: #134e4a; color: rgba(255,255,255,0.7); padding: 10px 36px; display: flex; justify-content: space-between; font-size: 8px; }', '.footer span { color: white; font-weight: 600; }', '@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { margin: 10mm; size: A4 landscape; } }'].join('\n');
  const styleTag = '<' + 'style' + '>' + cssText + '<' + '/style' + '>';
  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Comisiones</title>
  ${styleTag}
</head>
<body>
  <div class="header">
    <h1>Reporte de Comisiones</h1>
    <p>Savidna - Centro de Bienestar y Belleza</p>
    <div class="header-meta">
      <span>Período: ${dateFrom} — ${dateTo}</span>
      <span>${aptRows.length} comisiones de citas</span>
      <span>${productRows.length} comisiones de productos</span>
      <span>${today}</span>
    </div>
  </div>
  <div class="summary">
    <div class="summary-card">
      <label>Comisión Servicios</label>
      <strong>${fmt(totalServices)}</strong>
    </div>
    <div class="summary-card purple">
      <label>Comisión Productos</label>
      <strong>${fmt(totalProducts)}</strong>
    </div>
    <div class="summary-card pink">
      <label>Total General</label>
      <strong>${fmt(totalGeneral)}</strong>
    </div>
  </div>
  <div class="section">
    <div class="section-title">Comisiones por Citas</div>
    <table>
      <thead>
        <tr>
          <th>Fecha</th><th>Paciente</th><th>Doctor</th><th>Servicio</th>
          <th class="right">Total</th><th class="right">Comisión</th>
          <th class="center">Tipo</th><th>Usuario</th>
        </tr>
      </thead>
      <tbody>
        ${aptTableRows || '<tr class="empty-row"><td colspan="8">Sin registros en este período</td></tr>'}
      </tbody>
    </table>
  </div>
  <div class="section">
    <div class="section-title">Comisiones por Productos</div>
    <table>
      <thead>
        <tr>
          <th>Fecha</th><th>Producto</th>
          <th class="right">Venta</th><th class="right">Comisión</th><th>Usuario</th>
        </tr>
      </thead>
      <tbody>
        ${prodTableRows || '<tr class="empty-row"><td colspan="5">Sin registros en este período</td></tr>'}
      </tbody>
    </table>
  </div>
  <div class="footer">
    <span>Savidna - Documento Confidencial</span>
    <span>Reporte de Comisiones</span>
    <span>${today}</span>
  </div>
</body>
</html>`;
  // ── FIX: abrir en ventana nueva e imprimir como PDF ───────────────────────
  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 600);
  }
}
// ── EXPORT EXCEL (CSV con estilos via HTML) ───────────────────────────────────
function exportCommissionsToExcel(aptRows: any[], productRows: any[], dateFrom: string, dateTo: string) {
  const fmt = (n: number) => n.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP'
  });
  const totalSvc = aptRows.reduce((a, b) => a + b.commission, 0);
  const totalProd = productRows.reduce((a, b) => a + b.commission, 0);
  const totalGen = totalSvc + totalProd;
  const thStyle = `background:#134e4a;color:white;padding:6px 10px;font-size:11px;font-weight:bold;border:1px solid #0d9488;`;
  const tdStyle = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;`;
  const tdAmt = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;color:#059669;font-weight:bold;text-align:right;`;
  const tdRight = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;text-align:right;`;
  const tdEven = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;background:#f8fafc;`;
  const tdEvenR = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;background:#f8fafc;text-align:right;`;
  const tdEvenA = `padding:5px 10px;font-size:11px;border:1px solid #e2e8f0;background:#f8fafc;color:#059669;font-weight:bold;text-align:right;`;
  const aptTr = aptRows.map((r, i) => {
    const e = i % 2 === 0;
    return `<tr>
      <td style="${e ? tdEven : tdStyle}">${r.date}</td>
      <td style="${e ? tdEven : tdStyle}">${r.patient}</td>
      <td style="${e ? tdEven : tdStyle}">${r.doctor}</td>
      <td style="${e ? tdEven : tdStyle}">${r.service}</td>
      <td style="${e ? tdEvenR : tdRight}">${fmt(r.total)}</td>
      <td style="${e ? tdEvenA : tdAmt}">${fmt(r.commission)}</td>
      <td style="${e ? tdEven : tdStyle}">${r.type}</td>
      <td style="${e ? tdEven : tdStyle}">${r.userName}</td>
    </tr>`;
  }).join('');
  const prodTr = productRows.map((r, i) => {
    const e = i % 2 === 0;
    return `<tr>
      <td style="${e ? tdEven : tdStyle}">${r.date}</td>
      <td style="${e ? tdEven : tdStyle}">${r.product}</td>
      <td style="${e ? tdEvenR : tdRight}">${fmt(r.total)}</td>
      <td style="${e ? tdEvenA : tdAmt}">${fmt(r.commission)}</td>
      <td style="${e ? tdEven : tdStyle}">${r.userName}</td>
    </tr>`;
  }).join('');
  const html = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8">
  <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>
    <x:ExcelWorksheet><x:Name>Comisiones</x:Name>
    <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
<table style="font-family:Arial,sans-serif;border-collapse:collapse;width:100%;">
  <tr>
    <td colspan="8" style="background:#0d9488;color:white;font-size:16px;font-weight:bold;padding:14px 16px;">
      Reporte de Comisiones — ${dateFrom} al ${dateTo}
    </td>
  </tr>
  <tr>
    <td colspan="8" style="background:#134e4a;color:rgba(255,255,255,0.8);font-size:10px;padding:6px 16px;">
      Savidna - Centro de Bienestar y Belleza
    </td>
  </tr>
  <tr><td colspan="8" style="padding:6px;"></td></tr>
  <tr>
    <td colspan="2" style="background:#ecfdf5;border:1px solid #6ee7b7;padding:10px 14px;">
      <div style="font-size:9px;color:#6b7280;text-transform:uppercase;">Comisión Servicios</div>
      <div style="font-size:15px;font-weight:bold;color:#059669;">${fmt(totalSvc)}</div>
    </td>
    <td colspan="2" style="background:#f5f3ff;border:1px solid #c4b5fd;padding:10px 14px;">
      <div style="font-size:9px;color:#6b7280;text-transform:uppercase;">Comisión Productos</div>
      <div style="font-size:15px;font-weight:bold;color:#7c3aed;">${fmt(totalProd)}</div>
    </td>
    <td colspan="2" style="background:#fdf2f8;border:1px solid #f9a8d4;padding:10px 14px;">
      <div style="font-size:9px;color:#6b7280;text-transform:uppercase;">Total General</div>
      <div style="font-size:15px;font-weight:bold;color:#db2777;">${fmt(totalGen)}</div>
    </td>
    <td colspan="2" style="padding:0;"></td>
  </tr>
  <tr><td colspan="8" style="padding:8px;"></td></tr>
  <tr>
    <td colspan="8" style="background:#0f766e;color:white;font-size:11px;font-weight:bold;padding:8px 10px;text-transform:uppercase;letter-spacing:0.5px;">
      Comisiones por Citas (${aptRows.length} registros)
    </td>
  </tr>
  <tr>
    <th style="${thStyle}">Fecha</th>
    <th style="${thStyle}">Paciente</th>
    <th style="${thStyle}">Doctor</th>
    <th style="${thStyle}">Servicio</th>
    <th style="${thStyle};text-align:right;">Total</th>
    <th style="${thStyle};text-align:right;">Comisión</th>
    <th style="${thStyle}">Tipo</th>
    <th style="${thStyle}">Usuario</th>
  </tr>
  ${aptTr || `<tr><td colspan="8" style="${tdStyle}color:#9ca3af;font-style:italic;">Sin registros</td></tr>`}
  <tr><td colspan="8" style="padding:8px;"></td></tr>
  <tr>
    <td colspan="8" style="background:#0f766e;color:white;font-size:11px;font-weight:bold;padding:8px 10px;text-transform:uppercase;letter-spacing:0.5px;">
      Comisiones por Productos (${productRows.length} registros)
    </td>
  </tr>
  <tr>
    <th style="${thStyle}">Fecha</th>
    <th style="${thStyle}">Producto</th>
    <th style="${thStyle};text-align:right;">Venta</th>
    <th style="${thStyle};text-align:right;">Comisión</th>
    <th style="${thStyle}">Usuario</th>
    <td colspan="3"></td>
  </tr>
  ${prodTr || `<tr><td colspan="5" style="${tdStyle}color:#9ca3af;font-style:italic;">Sin registros</td></tr>`}
</table>
</body>
</html>`;
  const blob = new Blob(['\uFEFF' + html], {
    type: 'application/vnd.ms-excel;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `comisiones_${dateFrom}_${dateTo}.xls`;
  a.click();
  URL.revokeObjectURL(url);
}
// ── NORMALIZE ────────────────────────────────────────────────────────────────
function normalizeName(name: string) {
  if (!name) return '';
  return name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^(dr\.|dra\.|lic\.|ing\.|ter\.)\s*/g, '').replace(/\s+/g, ' ');
}
// ── COMPONENTE ───────────────────────────────────────────────────────────────
export function Commissions() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [sales, setSales] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [servicePercents, setServicePercents] = useState<Record<string, string>>({});
  const [productPercents, setProductPercents] = useState<Record<string, string>>({});
  const [saveMsg, setSaveMsg] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(today);
  const [selectedUser, setSelectedUser] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  useEffect(() => {
    loadAll();
  }, []);
  function buildPeopleList(usersData: any[], doctorsData: any[]) {
    const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
    const people: any[] = [];
    const seenNormalizedNames = new Set<string>();
    usersData.forEach((u: any) => {
      if (!allowedRoles.has((u.role || '').toLowerCase())) return;
      const norm = normalizeName(u.name);
      if (norm && !seenNormalizedNames.has(norm)) {
        seenNormalizedNames.add(norm);
        people.push({
          id: u.id,
          name: u.name,
          normalized: norm
        });
      }
    });
    doctorsData.forEach((d: any) => {
      const norm = normalizeName(d.name);
      if (norm && !seenNormalizedNames.has(norm)) {
        seenNormalizedNames.add(norm);
        people.push({
          id: d.id,
          name: d.name,
          normalized: norm
        });
      }
    });
    return people;
  }
  async function loadAll() {
    setLoading(true);
    try {
      const [configRes, patientsRes, doctorsRes, usersRes, servicesRes, appointmentsRes, salesRes, productsRes] = await Promise.all([supabase.from('commission_configs').select('*'), supabase.from('patients').select('*'), supabase.from('doctors').select('*'), supabase.from('users').select('id, name, role'), supabase.from('services').select('*'), supabase.from('appointments').select('*, patients:patient_id(id, name), doctors_rel:doctor_id(id, name), services_rel:service_id(id, name, price)'), supabase.from('product_sales').select('*'), supabase.from('products').select('id, name')]);
      const configs = configRes.data || [];
      const patientsData = patientsRes.data || [];
      const doctorsData = doctorsRes.data || [];
      const usersData = usersRes.data || [];
      const servicesData = servicesRes.data || [];
      const appointmentsData = appointmentsRes.data || [];
      const salesData = salesRes.data || [];
      const productsData = productsRes.data || [];
      const allPeople = buildPeopleList(usersData, doctorsData);
      const sp: Record<string, string> = {};
      const pp: Record<string, string> = {};
      const configByNormName: Record<string, {
        service: string;
        product: string;
      }> = {};
      const configById: Record<string, {
        service: string;
        product: string;
      }> = {};
      configs.forEach((row: any) => {
        const norm = normalizeName(row.person_name);
        const pid = String(row.person_id || '');
        const scope = row.commission_scope || 'service';
        const val = String(row.commission_percent || 0);
        if (norm) {
          if (!configByNormName[norm]) configByNormName[norm] = {
            service: '0',
            product: '0'
          };
          if (scope === 'service') configByNormName[norm].service = val;
          if (scope === 'product') configByNormName[norm].product = val;
        }
        if (pid) {
          if (!configById[pid]) configById[pid] = {
            service: '0',
            product: '0'
          };
          if (scope === 'service') configById[pid].service = val;
          if (scope === 'product') configById[pid].product = val;
        }
      });
      allPeople.forEach((p: any) => {
        const byId = configById[String(p.id)];
        const byName = configByNormName[normalizeName(p.name)];
        const found = byId || byName;
        sp[p.id] = found?.service || '0';
        pp[p.id] = found?.product || '0';
      });
      setUsers(allPeople);
      setServicePercents(sp);
      setProductPercents(pp);
      setPatients(patientsData);
      setDoctors(doctorsData);
      setServices(servicesData);
      setAppointments(appointmentsData);
      setSales(salesData);
      setProducts(productsData);
    } catch (err) {
      console.error('Error loading commissions:', err);
    } finally {
      setLoading(false);
    }
  }
  async function saveAll() {
    try {
      await supabase.from('commission_configs').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      const rows: any[] = [];
      users.forEach((u: any) => {
        rows.push({
          person_id: u.id,
          person_name: u.name,
          person_type: 'usuario',
          commission_scope: 'service',
          commission_percent: Number(servicePercents[u.id] || 0),
          is_active: true
        });
        rows.push({
          person_id: u.id,
          person_name: u.name,
          person_type: 'usuario',
          commission_scope: 'product',
          commission_percent: Number(productPercents[u.id] || 0),
          is_active: true
        });
      });
      if (rows.length > 0) {
        const {
          error: insertError
        } = await supabase.from('commission_configs').insert(rows);
        if (insertError) {
          setSaveMsg(`❌ Error al insertar: ${insertError.message}`);
          return;
        }
      }
      setSaveMsg('✅ Guardado correctamente ✓');
    } catch (err: any) {
      setSaveMsg(`❌ Error al guardar: ${err.message || 'Error desconocido'}`);
    }
    setTimeout(() => setSaveMsg(''), 3000);
    await loadAll();
  }
  const appointmentRows = useMemo(() => {
    return appointments.filter((x: any) => x.status === 'completed' || x.status === 'completada').flatMap((apt: any) => {
      const rows = [];
      const doctorObj = doctors.find((d: any) => String(d.id) === String(apt.doctor_id));
      const doctorName = apt.doctors_rel?.name || doctorObj?.name || apt.doctor_name || apt.doctorName || 'Doctor';
      const normDoctorName = normalizeName(doctorName);
      const commissionPersonDoc = users.find((u: any) => String(u.id) === String(apt.doctor_id) || normalizeName(u.name) === normDoctorName);
      const docCommissionId = commissionPersonDoc?.id || apt.doctor_id;
      const docPercent = Number(servicePercents[docCommissionId] || 0);
      const rawUserId = apt.created_by_user_id || apt.user_id;
      const rawUserName = apt.created_by_user_name || 'Usuario';
      const normUserName = normalizeName(rawUserName);
      const commissionPersonUser = users.find((u: any) => String(u.id) === String(rawUserId) || normalizeName(u.name) === normUserName);
      const userCommissionId = commissionPersonUser?.id || rawUserId;
      const userPercent = Number(servicePercents[userCommissionId] || 0);
      const patient = apt.patients?.name || patients.find((p: any) => String(p.id) === String(apt.patient_id))?.name || apt.patient_name || apt.patientName || 'Paciente';
      // ── HANDLE MULTIPLE SERVICES ──────────────────────────────────────────
      let serviceName = 'Servicio';
      let total = 0;
      // Check if appointment has multiple services in service_ids array
      if (apt.service_ids && Array.isArray(apt.service_ids) && apt.service_ids.length > 0) {
        // Multiple services: look up each service and sum prices
        const appointmentServices = apt.service_ids.map((sId: string) => services.find((s: any) => String(s.id) === String(sId))).filter(Boolean);
        if (appointmentServices.length > 0) {
          serviceName = appointmentServices.map((s: any) => s.name).join(', ');
          total = appointmentServices.reduce((sum: number, s: any) => sum + Number(s.price || 0), 0);
        } else {
          // Fallback if services not found in array
          const singleService = apt.services_rel || services.find((s: any) => String(s.id) === String(apt.service_id));
          serviceName = singleService?.name || apt.service_name || apt.serviceName || 'Servicio';
          total = Number(singleService?.price || apt.total || apt.amount || 0);
        }
      } else {
        // Single service (backward compatibility)
        const service = apt.services_rel || services.find((s: any) => String(s.id) === String(apt.service_id));
        serviceName = service?.name || apt.service_name || apt.serviceName || 'Servicio';
        total = Number(service?.price || apt.total || apt.amount || 0);
      }
      // ──────────────────────────────────────────────────────────────────────
      const date = (apt.date || apt.created_at || '').slice(0, 10);
      if (docPercent > 0) {
        rows.push({
          id: `${apt.id}-doc`,
          date,
          patient,
          doctor: doctorName,
          service: serviceName,
          total,
          commission: total * (docPercent / 100),
          userName: commissionPersonDoc?.name || doctorName,
          userId: docCommissionId,
          type: 'Doctor'
        });
      }
      if (userPercent > 0 && userCommissionId !== docCommissionId) {
        rows.push({
          id: `${apt.id}-user`,
          date,
          patient,
          doctor: doctorName,
          service: serviceName,
          total,
          commission: total * (userPercent / 100),
          userName: commissionPersonUser?.name || rawUserName,
          userId: userCommissionId,
          type: 'Gestión'
        });
      }
      if (rows.length === 0) {
        rows.push({
          id: apt.id,
          date,
          patient,
          doctor: doctorName,
          service: serviceName,
          total,
          commission: 0,
          userName: doctorName,
          userId: docCommissionId,
          type: 'Servicio'
        });
      }
      return rows;
    }).filter((x: any) => {
      if (x.date < dateFrom) return false;
      if (x.date > dateTo) return false;
      if (selectedUser !== 'all' && String(x.userId) !== String(selectedUser)) return false;
      return true;
    });
  }, [appointments, patients, doctors, services, users, servicePercents, dateFrom, dateTo, selectedUser]);
  const productRows = useMemo(() => {
    const productNameMap: Record<string, string> = {};
    products.forEach((p: any) => {
      productNameMap[String(p.id)] = p.name;
    });
    return sales.flatMap((sale: any) => {
      const rawUserId = sale.created_by_user_id || sale.user_id;
      const rawUserName = sale.created_by_user_name || 'Usuario';
      const normName = normalizeName(rawUserName);
      const commissionPerson = users.find((u: any) => String(u.id) === String(rawUserId) || normalizeName(u.name) === normName);
      const commissionId = commissionPerson?.id || rawUserId;
      const userName = commissionPerson?.name || rawUserName;
      const percent = Number(productPercents[commissionId] || 0);
      const date = (sale.date || sale.created_at || '').slice(0, 10);
      const items = Array.isArray(sale.items) ? sale.items : [];
      if (items.length === 0) {
        const total = Number(sale.total || 0);
        return [{
          id: sale.id,
          date,
          product: 'Venta de productos',
          total,
          commission: total * (percent / 100),
          userName,
          userId: commissionId
        }];
      }
      return items.map((item: any, idx: number) => {
        const productName = productNameMap[String(item.productId)] || item.productName || 'Producto';
        const subtotal = Number(item.subtotal || (item.unitPrice || 0) * (item.quantity || 0));
        return {
          id: `${sale.id}-${idx}`,
          date,
          product: `${productName} (x${item.quantity || 1})`,
          total: subtotal,
          commission: subtotal * (percent / 100),
          userName,
          userId: commissionId
        };
      });
    }).filter((x: any) => {
      if (x.date < dateFrom) return false;
      if (x.date > dateTo) return false;
      if (selectedUser !== 'all' && String(x.userId) !== String(selectedUser)) return false;
      return true;
    });
  }, [sales, products, users, productPercents, dateFrom, dateTo, selectedUser]);
  const totalServices = appointmentRows.reduce((a, b) => a + b.commission, 0);
  const totalProducts = productRows.reduce((a, b) => a + b.commission, 0);
  const totalGeneral = totalServices + totalProducts;
  const handleExportToExcel = async (exportDateFrom: string | null, exportDateTo: string | null) => {
    setIsExporting(true);
    await new Promise(r => setTimeout(r, 400));
    const from = exportDateFrom ?? dateFrom;
    const to = exportDateTo ?? dateTo;
    const filteredApt = exportDateFrom ? appointmentRows.filter(r => r.date >= from && r.date <= to) : appointmentRows;
    const filteredProd = exportDateFrom ? productRows.filter(r => r.date >= from && r.date <= to) : productRows;
    exportCommissionsToExcel(filteredApt, filteredProd, from, to);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (exportDateFrom: string | null, exportDateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(r => setTimeout(r, 400));
    const from = exportDateFrom ?? dateFrom;
    const to = exportDateTo ?? dateTo;
    const filteredApt = exportDateFrom ? appointmentRows.filter(r => r.date >= from && r.date <= to) : appointmentRows;
    const filteredProd = exportDateFrom ? productRows.filter(r => r.date >= from && r.date <= to) : productRows;
    const tSvc = filteredApt.reduce((a, b) => a + b.commission, 0);
    const tProd = filteredProd.reduce((a, b) => a + b.commission, 0);
    exportCommissionsToPdf(filteredApt, filteredProd, from, to, tSvc, tProd, tSvc + tProd);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const configPagination = usePagination(users, {
    itemsPerPage: 8
  });
  const aptPagination = usePagination(appointmentRows, {
    itemsPerPage: 10
  });
  const productPagination = usePagination(productRows, {
    itemsPerPage: 10
  });
  if (loading) return <div className="p-10 text-center" data-id="element-1389">Cargando...</div>;
  return <div className="space-y-6" data-id="element-1390">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1391">
        <h1 className="text-2xl md:text-3xl font-bold flex gap-2 items-center" data-id="element-1392">
          <PercentIcon className="w-7 h-7 md:w-8 md:h-8 text-indigo-600" data-id="element-1393" />
          Comisiones
        </h1>
        <div className="flex flex-wrap gap-2 items-center" data-id="element-1394">
          <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-1396" />} onClick={() => setIsPdfExportModalOpen(true)} data-id="element-1395">
            
            Exportar PDF
          </Button>
          <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1398" />} onClick={() => setIsExportModalOpen(true)} data-id="element-1397">
            
            Exportar Excel
          </Button>
          <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-1400" />} onClick={saveAll} data-id="element-1399">
            Guardar
          </Button>
        </div>
      </div>

      {saveMsg && <div className="text-green-600 font-medium" data-id="element-1401">{saveMsg}</div>}

      {/* CUADROS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1402">
        <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0 shadow-xl" data-id="element-1403">
          <CardContent className="p-6" data-id="element-1404">
            <p data-id="element-1405">Comisión Servicios</p>
            <h2 className="text-2xl md:text-4xl font-black mt-2" data-id="element-1406">
              {formatCurrency(totalServices)}
            </h2>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0 shadow-xl" data-id="element-1407">
          <CardContent className="p-6" data-id="element-1408">
            <p data-id="element-1409">Comisión Productos</p>
            <h2 className="text-2xl md:text-4xl font-black mt-2" data-id="element-1410">
              {formatCurrency(totalProducts)}
            </h2>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white border-0 shadow-xl" data-id="element-1411">
          <CardContent className="p-6" data-id="element-1412">
            <p data-id="element-1413">Total General</p>
            <h2 className="text-2xl md:text-4xl font-black mt-2" data-id="element-1414">
              {formatCurrency(totalGeneral)}
            </h2>
          </CardContent>
        </Card>
      </div>

      {/* FILTROS */}
      <Card data-id="element-1415">
        <CardHeader data-id="element-1416">
          <CardTitle data-id="element-1417">
            <FilterIcon className="w-4 h-4 inline mr-2" data-id="element-1418" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent data-id="element-1419">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1420">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border rounded-xl px-3 py-2" data-id="element-1421" />
            
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border rounded-xl px-3 py-2" data-id="element-1422" />
            
            <Select value={selectedUser} onChange={setSelectedUser} options={[{
            value: 'all',
            label: 'Todos'
          }, ...users.map((u: any) => ({
            value: u.id,
            label: u.name
          }))]} data-id="element-1423" />
            
          </div>
        </CardContent>
      </Card>

      {/* CONFIG */}
      <Card data-id="element-1424">
        <CardHeader data-id="element-1425">
          <CardTitle data-id="element-1426">Configuración</CardTitle>
        </CardHeader>
        <CardContent data-id="element-1427">
          <table className="w-full" data-id="element-1428">
            <thead data-id="element-1429">
              <tr className="bg-gray-50 border-b" data-id="element-1430">
                <th className="text-left px-4 py-3" data-id="element-1431">Usuario</th>
                <th className="text-center" data-id="element-1432">Servicios %</th>
                <th className="text-center" data-id="element-1433">Productos %</th>
              </tr>
            </thead>
            <tbody data-id="element-1434">
              {configPagination.paginatedItems.map((u: any) => <tr key={u.id} className="border-b" data-id="element-1435">
                  <td className="px-4 py-3" data-id="element-1436">{u.name}</td>
                  <td className="text-center" data-id="element-1437">
                    <input value={servicePercents[u.id] || ''} onChange={e => setServicePercents(prev => ({
                  ...prev,
                  [u.id]: e.target.value
                }))} className="border rounded-xl w-24 text-center px-2 py-1" data-id="element-1438" />
                  
                  </td>
                  <td className="text-center" data-id="element-1439">
                    <input value={productPercents[u.id] || ''} onChange={e => setProductPercents(prev => ({
                  ...prev,
                  [u.id]: e.target.value
                }))} className="border rounded-xl w-24 text-center px-2 py-1" data-id="element-1440" />
                  
                  </td>
                </tr>)}
            </tbody>
          </table>
          <Pagination {...configPagination} data-id="element-1441" />
        </CardContent>
      </Card>

      {/* TABLAS */}
      <Tabs tabs={[{
      id: 'citas',
      label: 'Comisión por Citas',
      content: <Card data-id="element-1443">
                <CardContent className="p-0 overflow-x-auto" data-id="element-1444">
                  <table className="w-full min-w-[950px]" data-id="element-1445">
                    <thead className="bg-gray-50 border-b" data-id="element-1446">
                      <tr data-id="element-1447">
                        <th className="px-4 py-3 text-left" data-id="element-1448">Fecha</th>
                        <th className="px-4 py-3 text-left" data-id="element-1449">Paciente</th>
                        <th className="px-4 py-3 text-left" data-id="element-1450">Doctor</th>
                        <th className="px-4 py-3 text-left" data-id="element-1451">Servicio</th>
                        <th className="px-4 py-3 text-right" data-id="element-1452">Total</th>
                        <th className="px-4 py-3 text-right" data-id="element-1453">Comisión</th>
                        <th className="px-4 py-3 text-center" data-id="element-1454">Tipo</th>
                        <th className="px-4 py-3 text-center" data-id="element-1455">Usuario</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1456">
                      {aptPagination.paginatedItems.map((row: any) => <tr key={row.id} className="border-b" data-id="element-1457">
                          <td className="px-4 py-3" data-id="element-1458">{row.date}</td>
                          <td className="px-4 py-3" data-id="element-1459">{row.patient}</td>
                          <td className="px-4 py-3" data-id="element-1460">{row.doctor}</td>
                          <td className="px-4 py-3" data-id="element-1461">{row.service}</td>
                          <td className="px-4 py-3 text-right" data-id="element-1462">
                            {formatCurrency(row.total)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-bold" data-id="element-1463">
                            {formatCurrency(row.commission)}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-1464">
                            <Badge variant={row.type === 'Doctor' ? 'success' : 'default'} data-id="element-1465">
                        
                              {row.type || 'Servicio'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-1466">
                            <Badge variant="outline" data-id="element-1467">{row.userName}</Badge>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                  <div className="p-4" data-id="element-1468">
                    <Pagination {...aptPagination} data-id="element-1469" />
                  </div>
                </CardContent>
              </Card>
    }, {
      id: 'productos',
      label: 'Comisión Productos',
      content: <Card data-id="element-1470">
                <CardContent className="p-0 overflow-x-auto" data-id="element-1471">
                  <table className="w-full" data-id="element-1472">
                    <thead className="bg-gray-50 border-b" data-id="element-1473">
                      <tr data-id="element-1474">
                        <th className="px-4 py-3 text-left" data-id="element-1475">Fecha</th>
                        <th className="px-4 py-3 text-left" data-id="element-1476">Producto</th>
                        <th className="px-4 py-3 text-right" data-id="element-1477">Venta</th>
                        <th className="px-4 py-3 text-right" data-id="element-1478">Comisión</th>
                        <th className="px-4 py-3 text-center" data-id="element-1479">Usuario</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1480">
                      {productPagination.paginatedItems.map((row: any) => <tr key={row.id} className="border-b" data-id="element-1481">
                          <td className="px-4 py-3" data-id="element-1482">{row.date}</td>
                          <td className="px-4 py-3" data-id="element-1483">{row.product}</td>
                          <td className="px-4 py-3 text-right" data-id="element-1484">
                            {formatCurrency(row.total)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-bold" data-id="element-1485">
                            {formatCurrency(row.commission)}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-1486">
                            <Badge data-id="element-1487">{row.userName}</Badge>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                  <div className="p-4" data-id="element-1488">
                    <Pagination {...productPagination} data-id="element-1489" />
                  </div>
                </CardContent>
              </Card>
    }]} data-id="element-1442" />
      

      {/* MODALES */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Comisiones" isExporting={isExporting} data-id="element-1490" />
      
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Comisiones a PDF" isExporting={isPdfExporting} data-id="element-1491" />
      
    </div>;
}